import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { ArrowLeft, CheckCircle, Crown, Sparkles, Clock, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

const PLANS = {
  premium_family: {
    id: 'premium_family',
    name: 'Premium Family',
    price: 12.99,
    period: 'month',
    features: [
      { icon: Sparkles, text: 'Unlimited AI Care Guide access' },
      { icon: Users, text: 'Unlimited Care Circle members' },
      { icon: Clock, text: 'Full Care Hours™ tracking' },
      { icon: Shield, text: 'Priority caregiver matching' },
      { icon: CheckCircle, text: 'Export timesheets PDF/CSV' },
      { icon: Crown, text: '24/7 priority support' },
    ]
  },
  featured_caregiver: {
    id: 'featured_caregiver',
    name: 'Featured Listing',
    price: 9.99,
    period: 'month',
    features: [
      { icon: Crown, text: 'Featured badge on profile' },
      { icon: Sparkles, text: 'Priority in search results' },
      { icon: Users, text: 'More visibility to families' },
      { icon: Shield, text: 'Verified caregiver badge' },
    ]
  }
};

export default function Subscription() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isCaregiver = user?.role === 'independent_caregiver';

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await api.post('/subscriptions/create-checkout', {
        plan: planId === 'premium_family' ? 'premium' : 'professional',
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const currentPlan = isCaregiver ? PLANS.featured_caregiver : PLANS.premium_family;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscription">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Subscription</h1>
            <p className="text-xs sm:text-sm text-slate-500">Manage your ElderLink plan</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Plan */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Your Current Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  user?.subscription_tier === 'premium' ? 'bg-teal-500' : 'bg-slate-200'
                }`}>
                  {user?.subscription_tier === 'premium' ? (
                    <Crown className="w-6 h-6 text-white" />
                  ) : (
                    <Sparkles className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {user?.subscription_tier === 'premium' ? 'Premium' : 'Free Plan'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {user?.subscription_tier === 'premium' 
                      ? 'Full access to all features'
                      : 'Limited AI messages and features'
                    }
                  </p>
                </div>
              </div>
              {user?.subscription_tier === 'premium' && (
                <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                  Active
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Section */}
        {user?.subscription_tier !== 'premium' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Free</h3>
                    <p className="text-sm text-slate-500">Basic features</p>
                  </div>
                </div>
                <div className="mb-6">
                  <span className="text-2xl sm:text-3xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-slate-400" />
                    5 AI messages per day
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-slate-400" />
                    Basic care profile
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-slate-400" />
                    View caregiver directory
                  </li>
                </ul>
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              </CardContent>
            </Card>

            {/* Premium Plan */}
            <Card className="border-2 border-teal-500 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                RECOMMENDED
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{currentPlan.name}</h3>
                    <p className="text-sm text-slate-500">Full access</p>
                  </div>
                </div>
                <div className="mb-6">
                  <span className="text-2xl sm:text-3xl font-bold text-slate-900">${currentPlan.price}</span>
                  <span className="text-slate-500">/{currentPlan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {currentPlan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                      <feature.icon className="w-4 h-4 text-teal-500" />
                      {feature.text}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(currentPlan.id)}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                  disabled={loading}
                  data-testid="subscribe-btn"
                >
                  {loading ? 'Processing...' : 'Upgrade to Premium'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Already Premium */}
        {user?.subscription_tier === 'premium' && (
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
            <CardContent className="p-4 sm:p-8 text-center">
              <Crown className="w-16 h-16 mx-auto mb-4 opacity-80" />
              <h2 className="text-2xl font-bold mb-2 font-['Manrope']">You're on Premium!</h2>
              <p className="text-teal-100 mb-6">
                Enjoy unlimited access to all ElderLink features.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {currentPlan.features.slice(0, 6).map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-white/90">
                    <feature.icon className="w-4 h-4" />
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-slate-900">Can I cancel anytime?</h4>
              <p className="text-sm text-slate-600 mt-1">
                Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-900">What payment methods do you accept?</h4>
              <p className="text-sm text-slate-600 mt-1">
                We accept all major credit cards through our secure payment processor, Stripe.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-900">Is there a free trial?</h4>
              <p className="text-sm text-slate-600 mt-1">
                The free plan lets you try ElderLink with limited features. Upgrade anytime for full access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
